import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  CalendarDays,
  ChevronRight,
  Clock,
  Eye,
  Layers,
  Lock,
  PlayCircle,
} from "lucide-react";
import AppHeader from "@/components/AppHeader";
import PageShell from "@/components/masters/PageShell";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { downloadCsv, toCsv } from "@/lib/csv";
import { formatUserAudit, formatClosedBy } from "@/lib/audit";
import { HistoryChip, SearchBox } from "@/components/masters/UI";

const fmtDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
  const dt = new Date(y, (m || 1) - 1, d || 1);
  return dt.toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const SessionsPage = () => {
  const navigate = useNavigate();
  const { isAdmin, isLocationAdmin, isSupervisor, isAsstSupervisor, selectedLocationId } = useAuth();

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (isAdmin && selectedLocationId && selectedLocationId !== "all") {
      params.location_id = selectedLocationId;
    }
    api
      .get("/attendance/sessions", { params })
      .then((r) => setSessions(r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [isAdmin, selectedLocationId]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return sessions;
    return sessions.filter((row) =>
      `${row.date} ${row.shift_name || ""} ${row.shift_code || ""}`
        .toLowerCase()
        .includes(s)
    );
  }, [q, sessions]);

  const open = (sid) => navigate(`/attendance/session/${sid}`);

  const exportList = () => {
    const csv = toCsv(filtered, [
      { header: "Date", get: (r) => r.date || "" },
      { header: "Shift", get: (r) => r.shift_name || "" },
      { header: "Shift Start", get: (r) => r.shift_start || "" },
      { header: "Shift End", get: (r) => r.shift_end || "" },
      { header: "Grace (min)", get: (r) => r.grace_minutes ?? "" },
      { header: "Scan-In Status", get: (r) => (r.status === "open" ? "Open" : "Closed") },
      {
        header: "Scan-Out Status",
        get: (r) =>
          r.scan_out_status === "closed"
            ? "Closed"
            : (r.outstanding_count ?? 0) > 0
            ? "Pending"
            : "Idle",
      },
      { header: "Logged In", get: (r) => r.present_count ?? 0 },
      { header: "Late", get: (r) => r.late_count ?? 0 },
      { header: "Scanned", get: (r) => r.total_scanned ?? 0 },
      { header: "Scanned Out", get: (r) => r.scanned_out_count ?? 0 },
      { header: "Closed By", get: (r) => formatUserAudit(r.closed_by) || "" },
    ]);
    downloadCsv(`sessions-history-${new Date().toISOString().slice(0, 10)}`, csv);
    toast.success(`Exported ${filtered.length} sessions`);
  };

  return (
    <div className="min-h-screen bg-vision-ice" data-testid="sessions-page">
      <AppHeader />

      <PageShell
        eyebrow="Attendance"
        title="Sessions history"
        subtitle={`All Attendance Sessions for your Location · ${sessions.length} total`}
        primaryAction={
          isSupervisor || isAsstSupervisor || isAdmin || isLocationAdmin ? (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={exportList}
                data-testid="sessions-export"
                className="btn-pill border bg-white px-4 py-2 text-sm font-semibold text-vision-ink shadow-card hover:bg-[color:var(--vision-ice)]"
                style={{ borderColor: "rgba(52,73,94,0.15)" }}
              >
                Export
              </button>
              <button
                type="button"
                onClick={() => navigate("/attendance")}
                className="btn-pill bg-[color:var(--vision-orange)] px-5 py-2.5 text-sm font-semibold text-white shadow-card hover:brightness-105"
                data-testid="sessions-open-today"
              >
                <PlayCircle className="mr-1 inline h-4 w-4" />
                Today&apos;s session
              </button>
            </div>
          ) : null
        }
        testId="sessions-shell"
      >
        <div className="mb-4 flex items-center justify-between gap-3">
          <SearchBox
            value={q}
            onChange={setQ}
            placeholder="Search by date or shift…"
            testId="sessions-search"
          />
          <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-vision-slate/60">
            Showing {filtered.length}
          </span>
        </div>

        {loading ? (
          <div
            className="rounded-2xl border bg-white p-10 text-center text-sm text-vision-slate/70 shadow-card"
            style={{ borderColor: "rgba(52,73,94,0.08)" }}
            data-testid="sessions-loading"
          >
            Loading sessions…
          </div>
        ) : filtered.length === 0 ? (
          <div
            className="rounded-2xl border bg-white p-10 text-center text-sm text-vision-slate/70 shadow-card"
            style={{ borderColor: "rgba(52,73,94,0.08)" }}
            data-testid="sessions-empty"
          >
            {sessions.length === 0
              ? "No sessions yet for your Location. Start one from Attendance."
              : "No sessions match this search."}
          </div>
        ) : (
          <div
            className="overflow-hidden rounded-2xl border bg-white shadow-card"
            style={{ borderColor: "rgba(52,73,94,0.08)" }}
            data-testid="sessions-list"
          >
            <div
              className="hidden grid-cols-[1.3fr_1.6fr_1fr_1fr_1fr_100px] gap-4 border-b bg-[color:var(--vision-ice)] px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-vision-slate/60 md:grid"
              style={{ borderColor: "rgba(52,73,94,0.08)" }}
            >
              <div>Date</div>
              <div>Shift</div>
              <div>Scan-In</div>
              <div>Scan-Out</div>
              <div>Scanned</div>
              <div className="text-right">Open</div>
            </div>

            {filtered.map((row) => (
              <SessionRow
                key={row.id}
                row={row}
                onOpen={() => open(row.id)}
                isAdmin={isAdmin}
                isLocationAdmin={isLocationAdmin}
                isSupervisor={isSupervisor}
                isAsstSupervisor={isAsstSupervisor}
              />
            ))}
          </div>
        )}
      </PageShell>
    </div>
  );
};

const SessionRow = ({ row, onOpen, isAdmin, isLocationAdmin, isSupervisor, isAsstSupervisor }) => {
  const scanInOpen = row.status === "open";
  const scanOutClosed = row.scan_out_status === "closed";
  const outstanding = row.outstanding_count ?? 0;
  const asstReadOnly = isAsstSupervisor && !scanInOpen;
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`sessions-row-${row.id}`}
      className="grid w-full grid-cols-1 items-center gap-3 border-t px-5 py-4 text-left transition-colors hover:bg-[color:var(--vision-peach)]/25 md:grid-cols-[1.3fr_1.6fr_1fr_1fr_1fr_100px] md:gap-4"
      style={{ borderColor: "rgba(52,73,94,0.06)" }}
    >
      <div>
        <div className="font-display text-sm font-bold text-vision-ink">
          {fmtDate(row.date)}
        </div>
        <div className="text-xs text-vision-slate/60 numeric-tabular">
          {row.date}
        </div>
      </div>

      <div>
        <div className="truncate font-display text-sm font-bold text-vision-ink">
          {row.shift_name}
        </div>
        <div className="text-xs text-vision-slate/60">
          <Clock className="mr-1 inline h-3 w-3" />
          {row.shift_start}–{row.shift_end} · grace {row.grace_minutes}m
        </div>
      </div>

      <div>
        <Chip
          tone={scanInOpen ? "success" : "slate"}
          icon={scanInOpen ? PlayCircle : Lock}
          label={scanInOpen ? "Open" : "Closed"}
          testId={`sessions-row-${row.id}-scanin`}
        />
        {formatClosedBy(row) && (
          <div
            className="mt-1 text-[11px] font-medium text-vision-slate/75"
            data-testid={`sessions-row-${row.id}-closed-by`}
          >
            {formatClosedBy(row)}
          </div>
        )}
      </div>

      <div>
        <Chip
          tone={scanOutClosed ? "navy" : outstanding > 0 ? "orange" : "slate"}
          icon={scanOutClosed ? Lock : Layers}
          label={
            scanOutClosed
              ? "Closed"
              : outstanding > 0
              ? `${outstanding} pending`
              : "Idle"
          }
          testId={`sessions-row-${row.id}-scanout`}
        />
      </div>

      <div>
        <div className="font-display text-sm font-bold text-vision-ink numeric-tabular">
          {row.total_scanned ?? 0}
        </div>
        <div className="text-[11px] text-vision-slate/60">
          <CalendarDays className="mr-1 inline h-3 w-3" />
          {row.present_count ?? 0}P · {row.late_count ?? 0} Late
        </div>
      </div>

      <div className="flex items-center justify-end gap-2">
        {(isSupervisor || isAdmin || isLocationAdmin) && (
          <span className="chip bg-[color:var(--vision-navy)] text-white">
            Open <ChevronRight className="h-3.5 w-3.5" />
          </span>
        )}
        {asstReadOnly && (
          <span
            className="chip"
            style={{
              backgroundColor: "rgba(0, 53, 158, 0.08)",
              color: "var(--vision-navy)",
              border: "1px solid rgba(0, 53, 158, 0.25)",
            }}
          >
            <Eye className="h-3.5 w-3.5" />
            View
          </span>
        )}
        {isAsstSupervisor && !asstReadOnly && (
          <span className="chip bg-[color:var(--vision-navy)] text-white">
            Open <ChevronRight className="h-3.5 w-3.5" />
          </span>
        )}
      </div>
    </button>
  );
};

const Chip = ({ tone, icon: Icon, label, testId }) => (
  <HistoryChip tone={tone} icon={Icon} label={label} testId={testId} />
);

export default SessionsPage;
